import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { format } from "date-fns";
import {
  FormTextInput,
  FormTextarea,
  FormSelect
} from "protocol-setup/components/FormComponents";
import {
  FormButtonGroup,
  FormButtonGroupHeader
} from "protocol-setup/components/FormComponents/FormButtonGroup";

import {
  OpenSuccessNotification,
  OpenErrorNotification
} from "protocol-setup/components/notification";
import { useRegContactList } from "protocol-setup/redux/useLovs";
import { defaultErrorMessage, saveProtocolInfoPathB } from "protocol-setup/redux/constants";
import { putWithAuth } from "protocol-setup/redux/makeFetch";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import text from "protocol-setup/constants/text";
import { InfoFormSchema } from "./regulatorySetupSchema";
import { requestTypeOptionsForReg } from "./Lovs";
import { regTabNames } from "./regHelper";


const onSubmit = async (
  data,
  onSuccessCallback,
  event,
  reqNum = "",
  showNotification = true
) => {
  event.preventDefault();
  const formattedData = {
    ...data,
    requestStatus: "DRAFT",
    formPartType: "REGULATORY",
    protSetupContacts: [],
    requestNumber: reqNum
  };

  formattedData.primaryContact.forEach((key) => {
    formattedData.protSetupContacts.push({
      perId: key,
      contactType: "PRIMARY_REGULATORY"
    });
  });

  delete formattedData.primaryContact;

  try {
    const { requestNumber } = await putWithAuth(
      saveProtocolInfoPathB,
      formattedData
    );
    if (requestNumber) {
      onSuccessCallback({ ...data, requestNumber });
      if (showNotification) {
        OpenSuccessNotification({
          message: formattedData.requestNumber
            ? text.regulatory.common.successUpdate.message
            : text.regulatory.common.successCreate.message,
          autoHide: true
        });
      }
      return true;
    } 
      OpenErrorNotification({
        message: defaultErrorMessage,
        autoHide: true
      });
      return false;
    
  } catch (error) {
    OpenErrorNotification({
      message: error?.message || defaultErrorMessage,
      autoHide: true
    });
    return false;
  }
};

export function InfoFormCore({
  defaultValues,
  contactList,
  requestNumber,
  fetchPsData,
  setDirtyTab,
  selectedRequest,
  isSubmitFormEnabled,
  isFormReadOnly
}) {
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    trigger,
    formState: { isDirty, dirtyFields }
  } = useForm({
    resolver: yupResolver(InfoFormSchema),
    defaultValues
  });
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setDirtyTab(isDirty);
  }, [isDirty]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (location.state?.showSuccessMessage) {
      OpenSuccessNotification({
        message: location.state.message || text.regulatory.common.successCreate.message,
        autoHide: true
      });

      // Clear the state to prevent showing the message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const memoizedSelectedRequest = useMemo(
    () => selectedRequest,
    [selectedRequest]
  );

  const onSuccess = (data) => {
    if (selectedRequest?.requestNumber) {
      fetchPsData();
      // Show notification for updates since we're not navigating away
      OpenSuccessNotification({
        message: text.regulatory.common.successUpdate.message,
        autoHide: true
      });
    } else {
      // Navigate with success message state
      history.push(`/protocol-setup/regulatory/${data.requestNumber}`, {
        showSuccessMessage: true,
        message: text.regulatory.common.successCreate.message
      });
    }
    reset(getValues());
  };
  const resetForm = () => reset(defaultValues);

  // Wrap onSubmit with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback, event) =>
      onSubmit(data, onSuccessCallback, event, requestNumber, false),
    selectedRequest?.requestStatus === "DRAFT"
      ? dirtyFields
      : { initialSave: true },
    regTabNames.PROTOCOL_INFORMATION,
    selectedRequest?.requestStatus === "DRAFT" ? "Updates" : "Initial Save"
  );

  const formTitle = "Protocol Information";
  return (
    <div
      className={`form-layout-wrapper mt-3 ${isFormReadOnly ? "read-only" : ""
        }`}
      data-testid="info-form"
    >
      <form
        data-testid="form"
        onSubmit={handleSubmit((data, event) => {
          const d = {
            ...data,
            requestNumber: memoizedSelectedRequest.requestNumber
          }
          return onSubmitWithHistory(
            d,
            onSuccess,
            event
          )
        }
        )}
      >
        <div className="form-layout-header">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroupHeader
              control={control}
              reset={resetForm}
              formTitle={formTitle}
            />
          )}
          <h4 className="form-layout-title invisible">
            Protocol Setup - Protocol Information
          </h4>
        </div>
        <div
          className={`form-layout-body p-0 ${isFormReadOnly ? "pointer-events-none" : ""
            }`}
        >
          <div className="invisible"></div>
          <ul>
            <li className="row form-sub-group-1 px-3">
              <div className="col-md-4 mr-4 ">
                <FormTextInput
                  name="requestDate"
                  section="regulatory"
                  form="infoForm"
                  control={control}
                  type="text"
                  required
                  readOnly
                />
              </div>
              <div className="col-md-4 mr-4"></div>
              <div className="col-md-4"></div>
            </li>
            <li className="row form-sub-group-2 px-3">
              <div className="col-md-4 mr-4 ">
                <FormTextarea
                  name="lpo"
                  section="regulatory"
                  form="infoForm"
                  control={control}
                  required
                  readOnly
                />
              </div>
              <div className="col-md-4 mr-4 ">
                <FormTextarea
                  name="protocolNumber"
                  section="regulatory"
                  form="infoForm"
                  control={control} 
                  required
                  readOnly
                />
              </div>
              <div className="col-md-4">
                <FormTextarea
                  name="protocolTitle"
                  section="regulatory"
                  form="infoForm"
                  control={control}
                  required
                  readOnly
                  rows={5}
                />
              </div>
            </li>
            <li className="row form-sub-group-3 px-3">
              <div className="col-md-4 mr-4 ">
                <FormSelect
                  name="requestType"
                  section="regulatory"
                  form="infoForm"
                  control={control}
                  options={requestTypeOptionsForReg}
                  required
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4 ">
                <FormSelect
                  name="primaryContact"
                  section="regulatory"
                  form="infoForm"
                  control={control}
                  isMulti
                  options={contactList?.map((item) => ({
                    value: item.perId,
                    label: `${item.lastName}, ${item.firstName} (${item.ctepPersonId})`
                  }))}
                  required
                  placeholder="Select an option"
                  readOnly={isFormReadOnly}
                  trigger={trigger}
                />
              </div>
              <div className="col-md-4"></div>
            </li>
          </ul>
          <div className="form-layout-footer pt-3 pb-0">
            {isFormReadOnly ? (
              <></>
            ) : (
              <FormButtonGroup
                control={control}
                reset={resetForm}
                formTitle={formTitle}
                requestNumber={selectedRequest?.requestNumber}
                formPartType={selectedRequest?.formPartType}
                isSubmitFormEnabled={isSubmitFormEnabled}
                onSuccess={onSuccess}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

function InfoForm({
  selectedRequest,
  requestNumber,
  setDirtyTab,
  fetchPsData,
  isSubmitFormEnabled,
  isFormReadOnly
}) {
  const today = format(new Date(), "dd-MMM-yyyy");
  const {
    contactList: { data = [] }
  } = useRegContactList(selectedRequest?.lpo);

  const defaultValues = useMemo(
    () => ({
      requestNumber: selectedRequest?.requestNumber || requestNumber || "",
      requestDate: selectedRequest?.requestDate || today,
      protocolNumber:
        selectedRequest?.protocol || selectedRequest?.protocolNumber || "",
      protocolTitle: selectedRequest?.protocolTitle,
      requestType: selectedRequest?.requestType || "",
      lpo: selectedRequest?.lpo || "",
      primaryContact:
        selectedRequest?.protSetupContacts?.map((contact) => contact.perId) || []
    }),
    [selectedRequest, requestNumber, today]
  );

  return (
    <InfoFormCore
      defaultValues={defaultValues}
      today={today}
      contactList={data}
      fetchPsData={fetchPsData}
      requestNumber={requestNumber}
      setDirtyTab={setDirtyTab}
      selectedRequest={selectedRequest}
      isSubmitFormEnabled={isSubmitFormEnabled}
      isFormReadOnly={isFormReadOnly}
    />
  );
}

export default InfoForm;
